import { useEffect, useRef, useCallback, useState } from 'react'
import * as Y from 'yjs'
import type { Idea, AgentEvent, ChatMessage } from '../types'
import { runAgentAgUiStream } from '../agui/streamAgentRun'
import type { AgentDiagram, HitlDecision, AgentChatMessage, RecommendedIdea } from '../agui/streamAgentTypes'
import { effectiveEventKind } from '../agui/agUiNormalization'
import { generateId } from '../lib/id'
import { publishRoomPipeline } from '../services/rooms'
import type { PipelinePause, RoomPipeline } from '../lib/pipeline'

const DEBOUNCE_MS = 3000
const MIN_IDEAS = 1
const REGENERATE_STATUSES = new Set(['ready', 'done', 'error', 'processing'])
// Um run remoto "running" mais velho que isso é considerado morto (aba fechada,
// crash) e deixa de bloquear os demais clientes. Pior caso real do pipeline ~4min.
const REMOTE_RUN_STALE_MS = 5 * 60 * 1000
// Só eventos de nível de step vão para o Yjs durante o run (payload pequeno);
// TEXT_MESSAGE_CONTENT ficaria pesado demais para sync contínuo.
const LIVE_SYNC_KINDS = new Set(['RUN_STARTED', 'RUN_FINISHED', 'STEP_STARTED', 'STEP_FINISHED', 'CUSTOM'])

// Chaves Yjs legadas: removidas no save após primeira regeneração pós-deploy.
const LEGACY_DIAGRAM_KEYS = ['mermaid', 'mermaid_c1', 'mermaid_c2', 'mermaid_c3', 'mermaid_c4'] as const

function findPipelinePause(events: AgentEvent[]): PipelinePause | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]
    if (event?.name === 'archforge.pipeline.paused' && event.value && typeof event.value === 'object') {
      return event.value as PipelinePause
    }
  }
  return null
}

function pipelineSnapshot(artifacts: Y.Map<Record<string, unknown>>, status: 'complete' | 'paused'): RoomPipeline {
  const pause = artifacts.get('pipeline_pause') as unknown as PipelinePause | undefined
  const adr = pause?.adrs?.[0]
  const storm = artifacts.get('event_storm') as unknown
  const diagrams = artifacts.get('diagrams') as unknown
  const proto = artifacts.get('prototype') as unknown
  const arch = artifacts.get('architecture') as unknown
  const hasStorm = Array.isArray(storm) && storm.length > 0
  const hasDiag = Array.isArray(diagrams) && diagrams.length > 0
  const hasProto = !!proto && typeof proto === 'object' && Object.keys(proto as object).length > 0
  const hasSdd = typeof arch === 'string' && arch.length > 0 && !arch.startsWith('[ERRO]')
  const pending = status === 'paused'
  return {
    segments: [1, hasStorm ? 1 : 0, pending ? 2 : hasSdd ? 1 : 0, hasDiag ? 1 : 0, hasProto ? 1 : 0],
    stage_label: pending ? 'SDD · pausado' : hasProto ? 'Completo' : hasDiag ? 'Diagramas' : hasSdd ? 'SDD' : hasStorm ? 'Event Storm' : 'Ideias',
    now: pending
      ? `Archie aguarda decisão${adr?.adr_id ? ` · ${adr.adr_id}` : ''}`
      : hasSdd
        ? 'Artefatos atualizados'
        : 'Aguardando geração',
    pending_hitl: pending,
    pending_adr_id: adr?.adr_id ?? null,
    pending_adr_title: adr?.adr_title ?? null,
    updated_at: new Date().toISOString(),
  }
}

export type AgentRunMode = 'auto' | 'regenerate'
/** Fluxo do pipeline. `resume` retoma depois de uma decisão HITL. */
export type RunFlow = 'brainstorm' | 'docs' | 'full' | 'resume'

export interface AgentObserverState {
  isRunning: boolean
  isRegenerating: boolean
  isQueued: boolean
  mode: AgentRunMode | null
  error: string | null
  lastRunStartedAt: string | null
  lastRunFinishedAt: string | null
  lastGeneratedAt: string | null
  /** True quando OUTRO participante da sala está executando o pipeline. */
  isRemoteRunning: boolean
  /** Início do run remoto (ISO) — para o timer da barra de status. */
  remoteRunStartedAt: string | null
  /** Há ideias adicionadas/editadas desde a última geração (aguardando regenerar). */
  hasPendingChanges: boolean
}

interface AgentRunResult {
  diagrams: AgentDiagram[]
  architecture: string
  prototype: Record<string, unknown> | null
  events: AgentEvent[]
  /** Fingerprint das ideias consolidadas neste run (persistido no Yjs). */
  ideasFingerprint?: string
  event_storm: any[]
  event_storm_is_fallback?: boolean
}

/** Status de ideia criada por um agente como sugestão (aguardando o usuário aceitar). */
export const SUGGESTED_STATUS = 'suggested'

// Fingerprint do CONJUNTO de ideias que o pipeline consolida (todas as ativas,
// não só as prontas). Ignora o status — muda quando ideias são adicionadas,
// editadas ou removidas. Usado para detectar mudanças pendentes desde a última
// geração e para dedupe do auto-run.
function computeFingerprint(ideas: Idea[]): string {
  const considered = ideas.filter((i) => REGENERATE_STATUSES.has(i.status))
  const parts = considered
    .map((i) => `${i.id}|${i.title}|${i.description || ''}|${i.category || ''}`)
    .sort()
  return parts.join(';;')
}

const INITIAL_STATE: AgentObserverState = {
  isRunning: false,
  isRegenerating: false,
  isQueued: false,
  mode: null,
  error: null,
  lastRunStartedAt: null,
  lastRunFinishedAt: null,
  lastGeneratedAt: null,
  isRemoteRunning: false,
  remoteRunStartedAt: null,
  hasPendingChanges: false,
}

export function useAgentObserver(
  ideas: Y.Array<Idea> | undefined,
  roomId: string,
  artifacts: Y.Map<Record<string, unknown>> | undefined,
  onStatus: (status: string) => void,
  eventStormElements?: Y.Array<Record<string, unknown>>,
  onEvents?: (events: AgentEvent[]) => void,
  /** Só o líder da sala dispara o run automático (eleição via awareness). */
  isLeader: boolean = true,
  /** Y.Array do chat — os agentes postam mensagens aqui ao fim do run. */
  chatMessages?: Y.Array<ChatMessage>,
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const runningRef = useRef(false)
  const lastFingerprintRef = useRef('')
  // Fingerprint das ideias no momento da última geração bem-sucedida — comparado
  // com o atual para sinalizar "há mudanças pendentes, regenere".
  const generatedFingerprintRef = useRef('')
  const queuedModeRef = useRef<AgentRunMode | null>(null)
  const onStatusRef = useRef(onStatus)
  onStatusRef.current = onStatus
  const onEventsRef = useRef(onEvents)
  onEventsRef.current = onEvents
  const isLeaderRef = useRef(isLeader)
  isLeaderRef.current = isLeader
  const chatMessagesRef = useRef(chatMessages)
  chatMessagesRef.current = chatMessages
  const liveSyncCountRef = useRef(0)

  const [state, setState] = useState<AgentObserverState>(INITIAL_STATE)

  // Persiste apenas os artefatos do fluxo que rodou, para não sobrescrever com vazio
  // o que pertence ao outro fluxo (brainstorm ↔ docs rodam separados).
  const saveArtifacts = useCallback(
    (result: AgentRunResult, flow: RunFlow) => {
      if (!artifacts) return
      const now = new Date().toISOString()

      const paused = findPipelinePause(result.events)
      const writesDocs = flow === 'docs' || flow === 'full' || flow === 'resume'
      if (writesDocs) {
        if (result.architecture) {
          artifacts.set('architecture', result.architecture as unknown as Record<string, unknown>)
        }
        // Pausa acontece antes de Jobs/Van Gogh: não apaga diagramas e protótipo já salvos.
        if (!paused) {
          artifacts.set('diagrams', result.diagrams as unknown as Record<string, unknown>)
          artifacts.set('prototype', result.prototype as unknown as Record<string, unknown>)
          for (const key of LEGACY_DIAGRAM_KEYS) {
            if (artifacts.has(key)) artifacts.delete(key)
          }
        }
      }

      if ((flow === 'brainstorm' || flow === 'full' || flow === 'resume') && !paused) {
        artifacts.set('event_storm', result.event_storm as unknown as Record<string, unknown>)
        artifacts.set('event_storm_is_fallback', Boolean(result.event_storm_is_fallback) as unknown as Record<string, unknown>)
        // Persiste Event Storm no Y.Array para colaboração; substitui só quando há elementos novos.
        if (eventStormElements && Array.isArray(result.event_storm) && result.event_storm.length > 0) {
          eventStormElements.delete(0, eventStormElements.length)
          eventStormElements.insert(0, result.event_storm as Record<string, unknown>[])
        }
      }

      if (paused) artifacts.set('pipeline_pause', paused as unknown as Record<string, unknown>)
      else if (artifacts.has('pipeline_pause')) artifacts.delete('pipeline_pause')

      artifacts.set('events', result.events as unknown as Record<string, unknown>)
      if (result.ideasFingerprint !== undefined) {
        artifacts.set('generated_fingerprint', result.ideasFingerprint as unknown as Record<string, unknown>)
      }
      artifacts.set('updated_at', now as unknown as Record<string, unknown>)
      artifacts.set('last_generated_at', now as unknown as Record<string, unknown>)
    },
    [artifacts, eventStormElements]
  )

  // Apenas em modo automático marcamos a ideia como "processing" para sinalizar pipeline.
  // Em modo regeneração mantemos o status original para não confundir o IdeaPanel.
  const markIdeasProcessing = useCallback(
    (ideaIds: string[]) => {
      if (!ideas) return
      const arr = ideas.toArray()
      for (let i = 0; i < arr.length; i++) {
        if (ideaIds.includes(arr[i].id) && arr[i].status === 'ready') {
          ideas.delete(i, 1)
          ideas.insert(i, [{ ...arr[i], status: 'processing' }])
        }
      }
    },
    [ideas]
  )

  const markIdeasDone = useCallback(
    (ideaIds: string[]) => {
      if (!ideas) return
      const arr = ideas.toArray()
      for (let i = 0; i < arr.length; i++) {
        if (ideaIds.includes(arr[i].id) && arr[i].status !== 'done') {
          ideas.delete(i, 1)
          ideas.insert(i, [{ ...arr[i], status: 'done' }])
        }
      }
    },
    [ideas]
  )

  const markIdeasError = useCallback(
    (ideaIds: string[]) => {
      if (!ideas) return
      const arr = ideas.toArray()
      for (let i = 0; i < arr.length; i++) {
        if (ideaIds.includes(arr[i].id) && arr[i].status !== 'done') {
          ideas.delete(i, 1)
          ideas.insert(i, [{ ...arr[i], status: 'error' }])
        }
      }
    },
    [ideas]
  )

  // Insere as mensagens de chat das personas e as ideias recomendadas no Yjs,
  // fazendo os agentes "interagirem" com a sala. Idempotente por geração:
  // remove as sugestões anteriores ainda não aceitas antes de inserir as novas.
  const insertAgentExtras = useCallback(
    (agentMessages?: AgentChatMessage[], recommended?: RecommendedIdea[]) => {
      const chat = chatMessagesRef.current
      if (chat && Array.isArray(agentMessages) && agentMessages.length > 0) {
        const now = new Date().toISOString()
        const toPush: ChatMessage[] = agentMessages.map((m) => ({
          id: generateId(),
          content: m.content,
          sender_id: m.agent_id,
          sender_name: m.agent_name,
          sender_type: 'agent',
          created_at: now,
        }))
        chat.push(toPush)
      }

      if (ideas && Array.isArray(recommended) && recommended.length > 0) {
        // Remove sugestões antigas ainda pendentes (status 'suggested') para não acumular.
        const arr = ideas.toArray()
        for (let i = arr.length - 1; i >= 0; i--) {
          if (arr[i].status === SUGGESTED_STATUS) ideas.delete(i, 1)
        }
        // Posiciona as sugestões numa coluna à direita das ideias existentes.
        const remaining = ideas.toArray()
        const baseX = remaining.length > 0 ? Math.max(...remaining.map((i) => i.x || 0)) + 300 : 80
        const suggestions: Idea[] = recommended.slice(0, 4).map((r, idx) => ({
          id: generateId(),
          title: r.title || 'Sugestão do agente',
          description: r.description || '',
          category: r.category || 'improvement',
          status: SUGGESTED_STATUS,
          x: baseX,
          y: 80 + idx * 150,
          created_by: 'Dochie (Analista Multidimensional)',
          created_at: new Date().toISOString(),
        }))
        ideas.push(suggestions)
      }
    },
    [ideas],
  )

  // Um run disparado por OUTRO cliente está ativo? (visível via Yjs)
  const isRemoteRunActive = useCallback(() => {
    if (!artifacts) return false
    if (runningRef.current) return false
    if ((artifacts.get('generation_status') as unknown) !== 'running') return false
    const startedAt = artifacts.get('generation_started_at')
    if (typeof startedAt !== 'string') return true
    const age = Date.now() - new Date(startedAt).getTime()
    return Number.isNaN(age) ? true : age < REMOTE_RUN_STALE_MS
  }, [artifacts])

  // Publica eventos de step no Yjs durante o stream para que os DEMAIS
  // participantes acompanhem o progresso ao vivo (não só o cliente que disparou).
  const syncLiveEvents = useCallback(
    (events: AgentEvent[]) => {
      if (!artifacts) return
      const compact = events.filter((e) => LIVE_SYNC_KINDS.has(effectiveEventKind(e ?? {})))
      if (compact.length === liveSyncCountRef.current) return
      liveSyncCountRef.current = compact.length
      artifacts.set('live_events', compact as unknown as Record<string, unknown>)
    },
    [artifacts]
  )

  const runAgent = useCallback(
    async (modeOverride?: AgentRunMode, flow: RunFlow = 'docs') => {
      if (runningRef.current) {
        if (modeOverride === 'regenerate') {
          queuedModeRef.current = 'regenerate'
          onStatusRef.current('⏳ Regeneração agendada para após a execução atual...')
          setState((prev) => ({ ...prev, isQueued: true, isRegenerating: true }))
        }
        return
      }
      if (!ideas) return
      // Outro participante já está gerando — não competir; o resultado chega via Yjs.
      if (isRemoteRunActive()) {
        if (modeOverride === 'regenerate') {
          onStatusRef.current('⏳ Outro participante já está gerando os artefatos...')
        }
        return
      }

      const token = localStorage.getItem('token')
      if (!token) {
        onStatusRef.current('🔒 Faça login para ativar o agente')
        return
      }

      const mode: AgentRunMode = modeOverride ?? 'auto'
      const isRegeneration = mode === 'regenerate'
      // O pipeline SEMPRE consolida todas as ideias ativas da sala (prontas, já
      // incluídas, em processamento ou com erro) — nunca só a última adicionada.
      // Antes o modo auto filtrava apenas `ready`, então ideias já processadas
      // (viram `done`) saíam do input e os artefatos refletiam só a ideia nova.
      const currentIdeas = ideas
        .toArray()
        .filter((i) => REGENERATE_STATUSES.has(i.status))

      if (currentIdeas.length < MIN_IDEAS) {
        setState((prev) => ({
          ...prev,
          isRunning: false,
          isRegenerating: false,
          isQueued: false,
          mode: null,
          error: isRegeneration ? 'Nenhuma ideia encontrada para regenerar.' : prev.error,
        }))
        if (isRegeneration) {
          onStatusRef.current('⚠️ Nenhuma ideia encontrada para regenerar')
        }
        return
      }

      const fingerprint = computeFingerprint(currentIdeas)
      if (!isRegeneration && fingerprint === lastFingerprintRef.current) {
        return
      }

      runningRef.current = true
      lastFingerprintRef.current = fingerprint
      const startedAt = new Date().toISOString()
      setState((prev) => ({
        ...prev,
        isRunning: true,
        isRegenerating: isRegeneration,
        isQueued: false,
        mode,
        error: null,
        lastRunStartedAt: startedAt,
        lastRunFinishedAt: null,
      }))
      if (artifacts) {
        artifacts.set('generation_status', 'running' as unknown as Record<string, unknown>)
        if (artifacts.has('pipeline_pause')) artifacts.delete('pipeline_pause')
        artifacts.set('generation_mode', mode as unknown as Record<string, unknown>)
        artifacts.set('generation_started_at', startedAt as unknown as Record<string, unknown>)
        artifacts.delete('generation_error')
      }
      onStatusRef.current(isRegeneration ? '🔄 Regenerando artefatos...' : '🤖 Analisando ideias...')

      const ideaIds = currentIdeas.map((i) => i.id)
      const previousIdeaStatus = new Map(currentIdeas.map((i) => [i.id, i.status]))
      if (!isRegeneration) {
        markIdeasProcessing(ideaIds)
      }
      let runFailed = false
      let runPaused = false
      let errorMessage: string | null = null

      // Decisões HITL registradas desde a última geração — o Archie as considera.
      const hitlDecisions = (artifacts?.get('hitl_decisions') ?? []) as HitlDecision[]
      liveSyncCountRef.current = 0

      try {
        const data = await runAgentAgUiStream({
          roomId,
          ideas: currentIdeas.map((i) => ({
            id: i.id,
            title: i.title,
            description: i.description,
            category: i.category,
          })),
          hitlDecisions: Array.isArray(hitlDecisions) ? hitlDecisions : [],
          mode: flow,
          context:
            flow === 'resume'
              ? { previous_architecture: String(artifacts?.get('architecture') || '') }
              : undefined,
          onEvents: (events) => {
            onEventsRef.current?.(events)
            syncLiveEvents(events)
          },
        })

        const events = data.events || []
        runPaused = Boolean(findPipelinePause(events))
        onStatusRef.current(
          runPaused
            ? '⏸️ Archie pausou: há uma decisão pendente'
            : isRegeneration
              ? '✅ Artefatos regenerados'
              : '✅ Arquitetura gerada',
        )
        if (onEventsRef.current) {
          onEventsRef.current(events)
        }
        saveArtifacts({
          diagrams: Array.isArray(data.diagrams) ? (data.diagrams as AgentDiagram[]) : [],
          architecture: data.architecture || '',
          prototype: data.prototype || null,
          events,
          event_storm: (data.event_storm || []) as any[],
          event_storm_is_fallback: data.event_storm_is_fallback,
          ideasFingerprint: fingerprint,
        }, flow)
        // Decisões HITL enviadas foram consumidas pela síntese — limpa o registro.
        if (artifacts && Array.isArray(hitlDecisions) && hitlDecisions.length > 0) {
          artifacts.delete('hitl_decisions')
        }
        if (!runPaused) markIdeasDone(ideaIds)
        // Agentes "vivos": postam mensagens no chat e sugerem ideias no canvas.
        insertAgentExtras(data.chat_messages, data.recommended_ideas)
        // Marca este conjunto de ideias como "consolidado": mudanças posteriores
        // acendem o aviso de pendência no botão Regenerar.
        generatedFingerprintRef.current = fingerprint
        setState((prev) => ({ ...prev, hasPendingChanges: false }))
      } catch (err: any) {
        runFailed = true
        const status =
          typeof err?.response?.status === 'number'
            ? err.response.status
            : typeof err?.status === 'number'
              ? err.status
              : undefined
        const detail = err.response?.data?.detail || ''
        if (isRegeneration && ideas) {
          const arr = ideas.toArray()
          for (let i = 0; i < arr.length; i++) {
            const previousStatus = previousIdeaStatus.get(arr[i].id)
            if (previousStatus && arr[i].status !== previousStatus) {
              ideas.delete(i, 1)
              ideas.insert(i, [{ ...arr[i], status: previousStatus }])
            }
          }
        } else {
          markIdeasError(ideaIds)
        }
        errorMessage =
          err.code === 'ECONNABORTED' || err.message?.includes('timeout')
            ? 'Tempo limite excedido. O agente pode estar sobrecarregado, tente novamente em instantes.'
            : status === 429
              ? 'Outra execução já está em andamento. Vamos tentar de novo automaticamente.'
              : status === 502 || status === 503
                ? 'Agente offline. Tente novamente.'
                : status === 401
                  ? 'Sessão expirada. Faça login novamente.'
                  : detail || 'Erro ao executar o agente.'
        if (artifacts) {
          artifacts.set('generation_status', 'error' as unknown as Record<string, unknown>)
          artifacts.set('generation_error', errorMessage as unknown as Record<string, unknown>)
        }
        if (status === 401) {
          onStatusRef.current(`🔒 Sessão expirada. Faça login novamente.`)
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
          setTimeout(() => {
            window.location.href = '/login'
          }, 2000)
        } else if (status === 429) {
          onStatusRef.current('⏳ Agente ocupado. Reagendando...')
          // Sinaliza fila para retry automático.
          if (isRegeneration) queuedModeRef.current = 'regenerate'
        } else {
          onStatusRef.current(`❌ ${errorMessage}`)
        }
      } finally {
        runningRef.current = false
        const queuedMode = queuedModeRef.current
        queuedModeRef.current = null
        const finishedAt = new Date().toISOString()
        setState((prev) => ({
          ...prev,
          isRunning: false,
          isRegenerating: queuedMode === 'regenerate',
          isQueued: queuedMode === 'regenerate',
          mode: queuedMode,
          error: errorMessage,
          lastRunFinishedAt: finishedAt,
          lastGeneratedAt: runFailed ? prev.lastGeneratedAt : finishedAt,
        }))
        if (artifacts && !runFailed) {
          const status = runPaused ? 'paused' : 'complete'
          artifacts.set('generation_status', status as unknown as Record<string, unknown>)
          artifacts.set('generation_finished_at', finishedAt as unknown as Record<string, unknown>)
          const snapshot = pipelineSnapshot(artifacts, status)
          void publishRoomPipeline(roomId, snapshot).catch(() => {
            // O dashboard relê na próxima visita; falha de snapshot não invalida o run.
          })
        }
        // Eventos ao vivo já cumpriram seu papel (o snapshot final está em 'events').
        if (artifacts && artifacts.has('live_events')) {
          artifacts.delete('live_events')
        }
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        const retryDelay = queuedMode ? (runFailed ? 5000 : 400) : DEBOUNCE_MS
        timeoutRef.current = setTimeout(() => {
          runAgent(queuedMode ?? undefined, flow)
        }, retryDelay)
      }
    },
    [
      ideas,
      roomId,
      artifacts,
      saveArtifacts,
      markIdeasProcessing,
      markIdeasDone,
      markIdeasError,
      isRemoteRunActive,
      syncLiveEvents,
      insertAgentExtras,
    ]
  )

  // "Gerar documentação" — fluxo pesado (SDD + diagramas + protótipo), sob demanda.
  const regenerate = useCallback(() => {
    if (runningRef.current) {
      queuedModeRef.current = 'regenerate'
      onStatusRef.current('⏳ Regeneração agendada para após a execução atual...')
      setState((prev) => ({ ...prev, isQueued: true, isRegenerating: true, error: null }))
      return
    }
    if (isRemoteRunActive()) {
      onStatusRef.current('⏳ Outro participante já está gerando os artefatos...')
      return
    }
    setState((prev) => ({ ...prev, isRegenerating: true, isQueued: false, mode: 'regenerate', error: null }))
    onStatusRef.current('🔄 Gerando documentação...')
    lastFingerprintRef.current = ''
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      runAgent('regenerate', 'full')
    }, 200)
  }, [runAgent, isRemoteRunActive])

  // Dispara o brainstorm (leve) manualmente — ex.: botão "Sugestões dos agentes".
  const runBrainstorm = useCallback(() => {
    if (runningRef.current || isRemoteRunActive()) return
    lastFingerprintRef.current = ''
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      runAgent('regenerate', 'brainstorm')
    }, 200)
  }, [runAgent, isRemoteRunActive])

  const resumeAfterHitl = useCallback(() => {
    if (runningRef.current || isRemoteRunActive()) return
    lastFingerprintRef.current = ''
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      runAgent('regenerate', 'resume')
    }, 200)
  }, [runAgent, isRemoteRunActive])

  const dismissError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
    if (artifacts) artifacts.delete('generation_error')
  }, [artifacts])

  // Hidrata lastGeneratedAt do snapshot Yjs em montagens posteriores.
  useEffect(() => {
    if (!artifacts) return
    const handler = () => {
      const value = artifacts.get('last_generated_at') || artifacts.get('updated_at')
      if (typeof value === 'string') {
        setState((prev) =>
          prev.lastGeneratedAt === value ? prev : { ...prev, lastGeneratedAt: value },
        )
      }
      // Hidrata o fingerprint consolidado (persistido por quem gerou) para que
      // este cliente também saiba detectar mudanças pendentes.
      const genFp = artifacts.get('generated_fingerprint')
      if (typeof genFp === 'string' && genFp !== generatedFingerprintRef.current) {
        generatedFingerprintRef.current = genFp
      }
    }
    artifacts.observe(handler)
    handler()
    return () => artifacts.unobserve(handler)
  }, [artifacts])

  // Espelha o run REMOTO (disparado por outro participante) no estado local,
  // para que a UI deste cliente também mostre progresso/bloqueie regeneração.
  useEffect(() => {
    if (!artifacts) return
    const handler = () => {
      const remoteActive = isRemoteRunActive()
      const startedAt = artifacts.get('generation_started_at')
      setState((prev) => {
        const remoteRunStartedAt = remoteActive && typeof startedAt === 'string' ? startedAt : null
        if (prev.isRemoteRunning === remoteActive && prev.remoteRunStartedAt === remoteRunStartedAt) {
          return prev
        }
        return { ...prev, isRemoteRunning: remoteActive, remoteRunStartedAt }
      })
    }
    artifacts.observe(handler)
    handler()
    return () => artifacts.unobserve(handler)
  }, [artifacts, isRemoteRunActive])

  useEffect(() => {
    if (!ideas) return
    const handler = () => {
      // Eleição de disparador: só o líder da sala agenda o run automático.
      // Os demais clientes acompanham o progresso via Yjs (live_events).
      if (!isLeaderRef.current) return
      if (isRemoteRunActive()) return
      // O auto-disparo cuida do BRAINSTORM (leve): gera o Event Storm + sugestões
      // na primeira vez que há ideias prontas. A documentação (pesada) fica sempre
      // sob demanda no botão. Isso evita reprocessar a cada ideia adicionada.
      const currentIdeas = ideas.toArray()
      const fingerprint = computeFingerprint(currentIdeas)

      const es = artifacts?.get('event_storm')
      const hasBrainstorm = Array.isArray(es) && es.length > 0

      // Já há brainstorm: não reprocessa sozinho. Só sinaliza pendência para o botão.
      if (hasBrainstorm) {
        const pending = !!generatedFingerprintRef.current && fingerprint !== generatedFingerprintRef.current
        setState((prev) => (prev.hasPendingChanges === pending ? prev : { ...prev, hasPendingChanges: pending }))
        return
      }

      const readyCount = currentIdeas.filter((i) => i.status === 'ready').length
      const processingCount = currentIdeas.filter((i) => i.status === 'processing').length
      if (readyCount < MIN_IDEAS) return
      if (processingCount > 0) return
      if (fingerprint === lastFingerprintRef.current) return

      onStatusRef.current('💡 Ideias prontas — o time está esboçando o domínio...')
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        runAgent('auto', 'brainstorm')
      }, DEBOUNCE_MS)
    }

    ideas.observe(handler)
    return () => {
      ideas.unobserve(handler)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [ideas, runAgent, isRemoteRunActive, artifacts])

  return { regenerate, runBrainstorm, resumeAfterHitl, dismissError, ...state }
}
